import * as DialogPrimitive from "@radix-ui/react-dialog";
import { forwardRef, type ReactNode } from "react";
import { Icon } from "./icon";
import { cn } from "@/lib/cn";

/**
 * Radix dialog: focus trap, escape-to-close and aria wiring come free.
 * Enter/exit is a 160ms scale+fade (transform and opacity only), which the
 * global reduced-motion rule flattens to an instant swap.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { wide?: boolean }
>(function DialogContent({ className, children, wide, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col",
          "rounded-xl border border-line bg-surface shadow-pop",
          "data-[state=open]:animate-pop-in",
          wide ? "max-w-3xl" : "max-w-lg",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-sunken hover:text-ink active:scale-[0.98]"
        >
          <Icon name="close" size={20} />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export function DialogHeader({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <div className="border-b border-line px-5 py-4 pr-14">
      <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-ink">
        {title}
      </DialogPrimitive.Title>
      {description && (
        <DialogPrimitive.Description className="mt-1 text-sm leading-relaxed text-muted">
          {description}
        </DialogPrimitive.Description>
      )}
    </div>
  );
}

export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", className)}>{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
      {children}
    </div>
  );
}
